import { Link } from "react-router-dom";
import { Mail, MapPin, Clock, MessageCircle, ArrowUpRight } from "lucide-react";
import { BETEC_CONTACT } from "./Contacto";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_betec-tech/artifacts/yy8fwelo_betec_logo_monocromatico.png";

const COLUMNS = [
  {
    title: "Soluciones",
    links: [
      { label: "Autoservicio", href: "/#soluciones" },
      { label: "POS", href: "/#soluciones" },
      { label: "Cartelería Digital", href: "/#soluciones" },
      { label: "Turnomáticos", href: "/#soluciones" },
      { label: "Redes & Fibra Óptica", href: "/#soluciones" },
    ],
  },
  {
    title: "Servicios",
    links: [
      { label: "Soporte 24/7", href: "/#servicios" },
      { label: "Despliegues Masivos", href: "/#servicios" },
      { label: "Renovación Tecnológica", href: "/#servicios" },
      { label: "Obras Civiles", href: "/#servicios" },
      { label: "Implementación & Obras", href: "/#servicios" },
      { label: "Certificación de Redes", href: "/#servicios" },
    ],
  },
  {
    title: "Empresa",
    links: [
      { label: "Nosotros", href: "/#nosotros" },
      { label: "Hardware", href: "/#hardware" },
      { label: "Contacto", href: "/#contacto" },
      { label: "Portal de Soporte", href: "/soporte", internal: true },
    ],
  },
];

const WA_LINK = `https://wa.me/${BETEC_CONTACT.whatsapp}?text=${encodeURIComponent(
  "Hola Betec, me gustaría conversar."
)}`;

export const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#030d1a] text-white pt-20 pb-10 overflow-hidden"
    >
      {/* Subtle pattern */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      <div
        aria-hidden
        className="absolute top-0 left-1/3 w-[420px] h-[260px] rounded-full bg-[#1FA08A]/15 blur-[140px]"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Top: Brand + Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-16">
          {/* Brand column */}
          <div className="lg:col-span-5">
            <Link
              to="/"
              data-testid="footer-logo"
              className="block mb-6"
              aria-label="Betec - Inicio"
            >
              <div className="bg-white rounded-md px-8 md:px-10 py-6 md:py-8 flex items-center justify-center w-full max-w-[460px]">
                <img
                  src={LOGO_URL}
                  alt="Betec"
                  className="h-24 md:h-28 w-auto object-contain"
                />
              </div>
            </Link>
            <p className="text-slate-300/80 text-[15px] leading-relaxed max-w-md mb-6">
              Infraestructura tecnológica y soporte especializado para
              empresas que no pueden detenerse. Hardware, redes, despliegues
              y operación 24/7 en todo Chile.
            </p>

            <div className="space-y-2.5">
              <FooterContact Icon={Mail} text={BETEC_CONTACT.email} href={`mailto:${BETEC_CONTACT.email}`} />
              <FooterContact
                Icon={MessageCircle}
                text="WhatsApp · Mesa de ayuda"
                href={WA_LINK}
                external
                accent
              />
              <FooterContact Icon={MapPin} text={BETEC_CONTACT.city} />
              <FooterContact
                Icon={Clock}
                text={`${BETEC_CONTACT.hours_office}  ·  ${BETEC_CONTACT.hours_support}`}
              />
            </div>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-10">
            {COLUMNS.map((col) => (
              <div key={col.title} data-testid={`footer-col-${col.title.toLowerCase()}`}>
                <h4 className="text-[11px] uppercase tracking-[0.22em] font-mono font-semibold text-[#2bc9ae] mb-5">
                  {col.title}
                </h4>
                <ul className="space-y-2.5">
                  {col.links.map((l) =>
                    l.internal ? (
                      <li key={l.label}>
                        <Link
                          to={l.href}
                          className="text-[14px] text-slate-300/80 hover:text-white transition-colors inline-flex items-center gap-1.5 group"
                        >
                          {l.label}
                          <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </Link>
                      </li>
                    ) : (
                      <li key={l.label}>
                        <a
                          href={l.href}
                          className="text-[14px] text-slate-300/80 hover:text-white transition-colors"
                        >
                          {l.label}
                        </a>
                      </li>
                    )
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Soporte cross-CTA */}
        <div
          data-testid="footer-support-cta"
          className="rounded-md border border-white/10 bg-white/[0.03] px-6 md:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 mb-12"
        >
          <div className="flex items-center gap-4">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#2bc9ae] opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#2bc9ae]" />
            </span>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.28em] font-mono font-semibold text-[#2bc9ae] mb-1">
                ¿Eres cliente con soporte activo?
              </div>
              <div className="font-heading text-white text-lg md:text-xl font-semibold tracking-tight">
                Levanta un ticket en el Portal de Soporte Betec.
              </div>
            </div>
          </div>
          <Link
            to="/soporte"
            data-testid="footer-support-link"
            className="inline-flex items-center gap-2 bg-white text-[#0A2A4E] hover:bg-[#2bc9ae] hover:text-white px-6 py-3 rounded-sm font-semibold text-[14px] tracking-wide transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap"
          >
            Abrir Portal de Soporte
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Bottom row */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12.5px]">
          <p className="text-slate-400">
            © {year} Betec.{" "}
            <span className="text-slate-500">
              Tecnología, infraestructura y operación crítica.
            </span>
          </p>
          <div className="flex items-center gap-5 text-slate-400">
            <span className="text-slate-500 font-mono uppercase tracking-[0.22em] text-[10.5px]">
              Hecho con estándar de retail
            </span>
            <span className="hidden md:inline-block w-px h-3 bg-white/15" />
            <a href="#inicio" className="hover:text-white transition-colors">
              Volver arriba ↑
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterContact = ({ Icon, text, href, external, accent }) => {
  const Wrap = href ? "a" : "div";
  const props = href
    ? external
      ? { href, target: "_blank", rel: "noopener noreferrer" }
      : { href }
    : {};
  return (
    <Wrap
      {...props}
      className={`group flex items-start gap-3 text-[13.5px] leading-snug ${
        accent
          ? "text-[#2bc9ae] hover:text-white"
          : "text-slate-300/85 hover:text-white"
      } ${href ? "transition-colors" : ""}`}
    >
      <Icon className="w-4 h-4 mt-0.5 shrink-0 opacity-80" strokeWidth={1.7} />
      <span>{text}</span>
    </Wrap>
  );
};

export default Footer;
